import type { opencodeToolIconKeyByName } from "schemas/agentTools/opencode";
import type {
  OpencodeTodowriteToolInput,
  OpencodeToolPart,
  SessionMessageWithParts,
} from "schemas/generated/agentTools/opencode";
import type {
  OpencodeToolDisplay,
  OpencodeToolDisplayContext,
  ToolDisplayText,
} from "./shared";

import { exhaustiveSwitch } from "replo-utils/lib/misc";
import { prettifySkillName } from "replo-utils/lib/string";
import { normalizeUrlIncludingProtocolRelative } from "replo-utils/lib/url";
import { opencodeTodowriteToolInputSchema } from "schemas/agentTools/opencode";

import {
  capitalizeFirstLetter,
  getFilePathDisplay,
  getRegularPastTense,
  getRegularProgressiveTense,
  getToolDisplayText,
  getToolInputString,
  quotedDetail,
  titleCase,
} from "./shared";

type DisplayTodoItem = OpencodeTodowriteToolInput["todos"][number] & {
  id: string;
};

export type TodoWriteState = {
  completedCount: number;
  isLatestTodoWrite: boolean;
  todos: DisplayTodoItem[];
  totalCount: number;
};

function getInputSummaryDisplay({
  getSummary,
  properties,
  toolPart,
}: {
  getSummary: (value: string | null) => ToolDisplayText;
  properties: readonly string[];
  toolPart: OpencodeToolPart;
}): OpencodeToolDisplay {
  return {
    summary: getSummary(getToolInputString({ properties, toolPart })),
    detail: null,
  };
}

function hasIrregularBashActionTense(
  action: string,
): action is keyof typeof IRREGULAR_BASH_ACTION_TENSE_BY_BASE {
  return Object.hasOwn(IRREGULAR_BASH_ACTION_TENSE_BY_BASE, action);
}

const IRREGULAR_BASH_ACTION_TENSE_BY_BASE = {
  read: { completed: "read", progressive: "reading" },
  write: { completed: "wrote", progressive: "writing" },
} as const;

const REGULAR_BASH_ACTIONS = new Set([
  "create",
  "delete",
  "edit",
  "list",
  "search",
  "update",
  "wait",
]);

function getBashActionTense({ action }: { action: string }) {
  const baseAction = action.toLowerCase();
  const irregularTense = hasIrregularBashActionTense(baseAction)
    ? IRREGULAR_BASH_ACTION_TENSE_BY_BASE[baseAction]
    : undefined;
  if (irregularTense === undefined && !REGULAR_BASH_ACTIONS.has(baseAction)) {
    return null;
  }

  return getActionTense({
    completed: irregularTense?.completed,
    action: baseAction,
    running: irregularTense?.progressive,
  });
}

function getActionTense({
  action,
  completed = getRegularPastTense({ verb: action }),
  running = getRegularProgressiveTense({ verb: action }),
}: {
  action: string;
  completed?: string;
  running?: string;
}) {
  return {
    completed: capitalizeFirstLetter({ value: completed }),
    running: capitalizeFirstLetter({ value: running }),
  };
}

function getDescriptionAction({ description }: { description: string }) {
  const [action, ...targetWords] = description.trim().split(/\s+/);
  if (!action || !/^[a-z]+$/i.test(action)) {
    return null;
  }

  return { action, target: targetWords.join(" ") };
}

function getBashDisplaySummary({ description }: { description: string }) {
  const descriptionAction = getDescriptionAction({ description });
  const actionTense = descriptionAction
    ? getBashActionTense({ action: descriptionAction.action })
    : null;
  if (!actionTense) {
    return getToolDisplayText({
      completed: `Ran ${description}`,
      pending: `Running ${description}`,
      running: `Running ${description}`,
    });
  }

  return getToolDisplayText({
    ...actionTense,
    target: descriptionAction?.target,
  });
}

export function getCurrentSessionToolParts(
  messages: readonly SessionMessageWithParts[],
) {
  return messages
    .flatMap((message) => message.parts)
    .flatMap((part) => (part.partData.type === "tool" ? [part.partData] : []));
}

export function getTodoWriteState({
  currentSessionToolParts = [],
  currentToolPart,
}: {
  currentToolPart: OpencodeToolPart;
} & OpencodeToolDisplayContext) {
  let todos: DisplayTodoItem[] = [];
  const todoWriteParts = currentSessionToolParts.filter((toolPart) => {
    return toolPart.tool.toLowerCase() === "todowrite";
  });

  for (const todoWritePart of todoWriteParts) {
    const parsedInput = opencodeTodowriteToolInputSchema.safeParse(
      todoWritePart.state.input,
    );
    if (!parsedInput.success) {
      continue;
    }
    todos = parsedInput.data.todos.map((todo, index) => {
      return {
        ...todo,
        id: `${todoWritePart.sessionID}:${index}`,
      };
    });

    if (todoWritePart.callID === currentToolPart.callID) {
      break;
    }
  }

  const completedCount = todos.filter(
    (todo) => todo.status === "completed",
  ).length;
  const totalCount = todos.length;
  return {
    completedCount,
    isLatestTodoWrite:
      todoWriteParts[todoWriteParts.length - 1]?.callID ===
      currentToolPart.callID,
    todos,
    totalCount,
  };
}

function getTodoWriteDisplay({
  currentSessionToolParts,
  currentToolPart,
}: {
  currentSessionToolParts: readonly OpencodeToolPart[];
  currentToolPart: OpencodeToolPart;
}): OpencodeToolDisplay {
  const todoWriteState = getTodoWriteState({
    currentToolPart,
    currentSessionToolParts,
  });

  return {
    summary: "Plan",
    detail:
      todoWriteState.totalCount === 0
        ? null
        : `${todoWriteState.completedCount}/${todoWriteState.totalCount} steps done`,
  };
}

function getEditFamilyToolDisplay({
  toolPart,
}: {
  toolPart: OpencodeToolPart;
}): OpencodeToolDisplay {
  return getFilePathDisplay({
    prefix: getToolDisplayText({
      completed: "Edited",
      pending: "Editing",
      running: "Editing",
    }),
    toolPart,
  });
}

export function getMainOpencodeToolDisplay({
  toolName,
  toolPart,
  toolParts,
}: {
  toolName: keyof typeof opencodeToolIconKeyByName;
  toolPart: OpencodeToolPart;
  toolParts: readonly OpencodeToolPart[];
}): OpencodeToolDisplay {
  return exhaustiveSwitch({ type: toolName })<OpencodeToolDisplay>({
    apply_patch: () => getEditFamilyToolDisplay({ toolPart }),
    bash: () => {
      const description = getToolInputString({
        properties: ["description"],
        toolPart,
      });
      return {
        summary: !description
          ? getToolDisplayText({
              completed: "Ran command",
              pending: "Running command",
              running: "Running command",
            })
          : getBashDisplaySummary({ description }),
        detail: null,
      };
    },
    edit: () => getEditFamilyToolDisplay({ toolPart }),
    multiedit: () => getEditFamilyToolDisplay({ toolPart }),
    patch: () => getEditFamilyToolDisplay({ toolPart }),
    // Zero-MCP-call scripts only; calls fan out to rows in getAgentToolModels.
    execute: () => {
      return {
        summary: getToolDisplayText({
          completed: "Ran code",
          pending: "Running code",
          running: "Running code",
        }),
        detail: null,
      };
    },
    glob: () => {
      return getInputSummaryDisplay({
        getSummary: (pattern) => {
          return !pattern
            ? getToolDisplayText({
                completed: "Found files",
                pending: "Finding files",
                running: "Finding files",
              })
            : getToolDisplayText({
                completed: `Found files "${pattern}"`,
                pending: `Finding files "${pattern}"`,
                running: `Finding files "${pattern}"`,
              });
        },
        properties: ["pattern"],
        toolPart,
      });
    },
    grep: () => {
      return getInputSummaryDisplay({
        getSummary: (pattern) => {
          return !pattern
            ? getToolDisplayText({
                completed: "Searched",
                pending: "Searching",
                running: "Searching",
              })
            : getToolDisplayText({
                completed: `Searched for "${pattern}"`,
                pending: `Searching for "${pattern}"`,
                running: `Searching for "${pattern}"`,
              });
        },
        properties: ["pattern"],
        toolPart,
      });
    },
    question: () => ({ summary: null, detail: null }),
    read: () => {
      return getFilePathDisplay({
        prefix: getToolDisplayText({
          completed: "Read",
          pending: "Reading",
          running: "Reading",
        }),
        toolPart,
      });
    },
    skill: () => {
      return getInputSummaryDisplay({
        getSummary: (skillName) => {
          if (!skillName) {
            return getToolDisplayText({
              completed: "Loaded skill",
              pending: "Loading skill",
              running: "Loading skill",
            });
          }
          const prettyName = prettifySkillName(skillName);
          return getToolDisplayText({
            completed: `Loaded skill "${prettyName}"`,
            pending: `Loading skill "${prettyName}"`,
            running: `Loading skill "${prettyName}"`,
          });
        },
        properties: ["name"],
        toolPart,
      });
    },
    task: () => {
      const subagentType = getToolInputString({
        properties: ["subagent_type"],
        toolPart,
      });
      const description = getToolInputString({
        properties: ["description"],
        toolPart,
      });
      // Untyped tasks get a neutral, user-facing label rather than the internal
      // "Specialist"/"Subagent" wording (REPL-31446: "wtf is specialist").
      const summary = subagentType
        ? titleCase({ value: subagentType })
        : "Working on a task";
      return {
        detail: quotedDetail({ value: description }),
        // Render the description verbatim, with no "Working on:"/"Completed:"
        // prefix — the surrounding UI (section header, activity meter, and
        // completion check) already conveys running vs. done, so the prefix is
        // redundant. Conjugating free-form model prose mangles nouns/acronyms.
        header: description || summary,
        summary,
      };
    },
    todowrite: () => {
      return getTodoWriteDisplay({
        currentSessionToolParts: toolParts,
        currentToolPart: toolPart,
      });
    },
    webfetch: () => {
      return getInputSummaryDisplay({
        getSummary: (url) => {
          const normalizedUrl = url
            ? normalizeUrlIncludingProtocolRelative(url)
            : null;
          const hostnameOrUrl = normalizedUrl?.hostname ?? url;
          return !url
            ? getToolDisplayText({
                completed: "Read webpage",
                pending: "Reading webpage",
                running: "Reading webpage",
              })
            : getToolDisplayText({
                completed: `Read ${hostnameOrUrl}`,
                pending: `Reading ${hostnameOrUrl}`,
                running: `Reading ${hostnameOrUrl}`,
              });
        },
        properties: ["url"],
        toolPart,
      });
    },
    write: () => {
      return getFilePathDisplay({
        prefix: getToolDisplayText({
          completed: "Wrote",
          pending: "Writing",
          running: "Writing",
        }),
        toolPart,
      });
    },
  });
}
